import { QuestionType } from 'commons';
import {
  consignmentDetailsFactory,
  consignmentGetFactory,
  consignmentQuestionFactory,
} from 'factories';
import { requestAxios } from 'services';
import {
  CommonResponse,
  Consignment,
  ConsignmentDetails,
  ConsignmentQuestion,
  PointOfSale,
} from 'types';

interface GetParams {
  startDate?: number;
  endDate?: number;
  codes?: string;
  cpf?: string;
  email?: string;
  ean?: string;
  storeNames?: string;
  statuses?: string;
  salesModes?: string;
  deliveryModes?: string;
  lastModifiedByTime?: number;
}

async function get(params: GetParams): Promise<Consignment[]> {
  let result: Consignment[] = [];
  params.storeNames = params.storeNames || undefined;

  try {
    const response = await requestAxios.get('consignments', { params });

    result = response.data;
  } catch (error) {
    console.error(error);
  }

  return consignmentGetFactory(result);
}

interface GetLastModificationParams {
  store?: string;
  seconds: number;
}

async function getLastModification(params: GetLastModificationParams) {
  let result: number = 0;

  try {
    const response = await requestAxios.get('consignments/lastModification', {
      params,
    });

    result = response.data;
  } catch (error) {
    console.error(error);
  }

  return result;
}

async function getDetails(
  consignmentCode: string
): Promise<ConsignmentDetails> {
  let result;

  try {
    const response = await requestAxios.get(
      `consignments/${consignmentCode}/details`
    );

    result = response.data.data[0];
  } catch (error) {
    console.error(error);
  }

  return consignmentDetailsFactory(result);
}

interface PutReserveProps {
  consignmentCode: string;
  store: string;
}

async function putReserve({
  consignmentCode,
  store,
}: PutReserveProps): Promise<CommonResponse> {
  let result: any = [];

  try {
    const response = await requestAxios.put(
      `consignments/${consignmentCode}/reserve?store=${store}`
    );

    result = response;
  } catch (error: any) {
    result = error?.response?.data;
    console.error(error);
  }

  return result;
}

interface PutConfirmProps {
  consignmentCode: string;
  productEans: { product: string; ean: string }[];
  nfeNumber?: string | undefined;
  nfeKey?: string | undefined;
  pointOfSale: PointOfSale;
  store: string;
  employeeDocument: string | null;
}

async function putConfirm({
  consignmentCode,
  productEans,
  nfeNumber,
  nfeKey,
  pointOfSale,
  store,
  employeeDocument,
}: PutConfirmProps): Promise<CommonResponse> {
  let result: any;
  const params = {
    store,
    nfeNumber,
    nfeKey,
    pointOfSale,
    employeeDocument,
  };

  try {
    const response = await requestAxios.put(
      `consignments/${consignmentCode}/confirm`,
      { productEans },
      { params }
    );

    result = response;
  } catch (error) {
    console.error(error);
  }

  return result;
}

interface PostCollectProps {
  consignmentCode: string;
  store: string;
  action: string;
  name: string;
  cpf?: string | null;
  deliveryMode?: string | null;
  pickUpAuthCode?: string | null;
  deliveryFailReason?: string | null;
  invoice?: string | null;
  receiptImage?: string;
}

async function postCollect({
  consignmentCode,
  store,
  action,
  name,
  cpf,
  deliveryMode,
  pickUpAuthCode,
  deliveryFailReason,
  invoice,
  receiptImage,
}: PostCollectProps): Promise<CommonResponse> {
  let result: any;
  const params = {
    store,
    action,
    name,
    cpf,
    deliveryMode,
    pickUpAuthCode,
    deliveryFailReason,
    nfeNumber: invoice,
  };

  const body = receiptImage ? { digitalAssign: receiptImage } : undefined;

  try {
    const response = await requestAxios.post(
      `consignments/${consignmentCode}/returnCollect`,
      body,
      { params, headers: { 'Content-Type': 'application/json' } }
    );

    result = response;
  } catch (error) {
    console.error(error);
  }

  return result;
}

interface PutRefuseProps {
  consignmentCode: string;
  store: string;
  reason: string;
  description?: string;
  selectedProducts: string[];
}

async function putRefuse({
  consignmentCode,
  store,
  reason,
  description,
  selectedProducts,
}: PutRefuseProps): Promise<CommonResponse> {
  let result: any;
  const body = {
    description,
    selectedProducts,
  };

  const params = {
    store,
    reason,
  };

  try {
    const response = await requestAxios.put(
      `consignments/${consignmentCode}/refuse`,
      body,
      { params }
    );

    result = response;
  } catch (error: any) {
    console.error(error);
  }

  return result;
}

interface GetDeliveryFailedProps {
  store: string;
}

async function getDeliveryFailed({
  store,
}: GetDeliveryFailedProps): Promise<ConsignmentQuestion[]> {
  const response = await requestAxios.get('deliveryFailed/questions', {
    params: { store },
  });

  return consignmentQuestionFactory(response.data.data);
}

interface PostDeliveryFailedProps {
  consignmentCode: string;
  answer: QuestionType;
  observation: string;
}

async function postDeliveryFailed(body: PostDeliveryFailedProps): Promise<any> {
  const response = await requestAxios.post('deliveryFailed', body);

  return response;
}

export const consignmentService = {
  get,
  getLastModification,
  getDetails,
  putReserve,
  putConfirm,
  postCollect,
  putRefuse,
  getDeliveryFailed,
  postDeliveryFailed,
};
